import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getMerchantDetails,
  getMerchantWithActions,
  getTaggedTerminals,
} from "../../../services/services";
import Label from "../../../components/Label";
import { parseDate, parseDateTime } from "../../../utils/tableFunctions";
import { Clear, Edit } from "@mui/icons-material";
import TableView from "../../../components/Table";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import { styles } from "../../../utils/styles";
import { MAPPED_REGISTERED_COUNTRY } from "../../../utils/constants";
import { APP_USER_SESSION } from "../../../constant/AppConstants";
import { StorageUtils } from "../../../utils";
import EditDialog from "../../../components/Admin-Terminal/Dialogs/EditDialog";

const MERCHANT_FIELDS = [
  { key: "name", label: "Merchant Name", uppercase: true },
  { key: "mid", label: "MID" },
  { key: "business_entity", label: "Business Entity", type: "businessEntity" },
  { key: "registered_country", label: "Registered Country", type: "country" },
  { key: "mcc", label: "Merchant Category Code", type: "mcc" },
  { key: "bank_account", label: "Bank Account", type: "bankAccount" },
  { key: "is_suspended", label: "Suspended", type: "boolean", positiveIsError: true },
  { key: "can_refund", label: "Allow Refund", type: "boolean" },
  { key: "can_void", label: "Allow Void", type: "boolean" },
  { key: "can_merchant_void", label: "Allow Merchant Void", type: "boolean" },
  { key: "can_merchant_refund", label: "Allow Merchant Refund", type: "boolean" },
  { key: "address_line1", label: "Address 1" },
  { key: "address_line2", label: "Address 2" },
  { key: "address_line3", label: "Address 3" },
  { key: "city", label: "City" },
  { key: "postal_code", label: "Postal code" },
  { key: "min_payout_amount", label: "Minimum Payout Amount", type: "payout" },
];

const comparableValue = (field, merchant) => {
  if (!merchant) return null;

  const value = merchant[field.key];
  switch (field.type) {
    case "businessEntity":
      return value?.id ?? value?.business_name ?? null;
    case "country":
      return value?.id ?? value?.registered_country ?? null;
    case "mcc":
      return value?.id ?? value?.code ?? null;
    case "bankAccount":
      return value
        ? value.id ?? `${value.account_number || ""}|${value.bank || ""}`
        : null;
    case "payout":
      return JSON.stringify(
        Array.isArray(value)
          ? [...value]
              .map(({ currency, min_amount }) => ({ currency, min_amount }))
              .sort((a, b) => String(a.currency).localeCompare(String(b.currency)))
          : []
      );
    default:
      return value ?? null;
  }
};

const MerchantDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [merchantDetails, setMerchantDetails] = useState();
  const [taggedTerminals, setTaggedTerminals] = useState();
  const [tableData, setTableData] = useState();
  const [loaded, setLoaded] = useState();
  const [selectedId, setSelectedId] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION)?.role || {};
  const userRoleTerminal = userRole?.terminal || [];

  useEffect(() => {
    if (!detailsData && !action) {
      getMerchantDetails(id, setMerchantDetails, setLoaded);
      getTaggedTerminals(id, setTaggedTerminals);
      return;
    }
    if (!detailsData && action) {
      getMerchantWithActions(id, setMerchantDetails, setLoaded);
      return;
    }

    setMerchantDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const handleBack = () => window.close();

  const handleCloseDialog = () => {
    selectedId && setSelectedId();
  };

  const formatData = (input) => {
    let tableData = [];
    input.results.forEach(({ id, is_active, mode, terminal_id, merchant }) => {
      tableData.push({
        id: id,
        terminal_id: terminal_id,
        mode: mode && mode.mode,
        merchant: merchant && merchant.name,
        is_active: is_active ? "Active" : "Inactive",
        action: (
          <>
            {
              is_active && userRoleTerminal.includes("Edit") && 
              <Tooltip title="Edit" placement="top">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(id);
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            }
          </>
        )
      });
    });
    return tableData;
  };

  // const getNext = async () => {
  //   if (!next) return;
  //   const res = await getNextPage({
  //     url: next,
  //     filter: filter,
  //   });
  //   let newRows = formatData(res);
  //   setTableData((old) => [...old, ...newRows]);
  //   setNext(res.next ? res.next : false);
  // };

  useEffect(() => {
    if (taggedTerminals) {
      setTableData(formatData(taggedTerminals));
      // setNext(taggedTerminals.next);
    }
  }, [taggedTerminals]);

  const renderFieldValue = (field, merchant) => {
    if (!merchant || merchant[field.key] === null || merchant[field.key] === undefined) {
      return "N/A";
    }

    const value = merchant[field.key];
    switch (field.type) {
      case "businessEntity":
        return value?.id ? (
          <Box
            component="span"
            sx={{
              overflowWrap: "anywhere",
              textDecoration: "underline",
              cursor: "pointer",
              color: "#1b6ac9",
            }}
            onClick={() =>
              window.open(`/3/app/entity-details/${value.id}/`, "_blank")
            }
          >
            {value.business_name || "N/A"}
          </Box>
        ) : value?.business_name || "N/A";
      case "country":
        return (
          MAPPED_REGISTERED_COUNTRY[value?.registered_country] ||
          value?.registered_country ||
          "N/A"
        );
      case "mcc":
        return value?.code
          ? `${value.code}${value.description ? ` - ${value.description}` : ""}`
          : "N/A";
      case "bankAccount":
        return value?.account_number
          ? `${value.account_number}${value.bank ? ` (${value.bank})` : ""}`
          : "N/A";
      case "boolean":
        return (
          <Label
            variant="ghost"
            color={
              field.positiveIsError
                ? value
                  ? "error"
                  : "success"
                : value
                ? "success"
                : "error"
            }
          >
            {value ? "YES" : "NO"}
          </Label>
        );
      case "payout":
        return Array.isArray(value) && value.length > 0
          ? value.map((item, index) => (
              <Box key={`${item.currency}-${index}`}>
                {item.currency}: {item.min_amount}
              </Box>
            ))
          : "N/A";
      default:
        return field.uppercase && typeof value === "string"
          ? value.toUpperCase()
          : value || "N/A";
    }
  };

  const InfoColumn = ({
    merchant,
    comparisonMerchant,
    title,
    emptyMessage,
    greyedOut = false,
    comparisonSide,
    forceHighlightAll = false,
  }) => {
    return (
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid",
            borderColor: greyedOut ? "grey.400" : "#999",
            borderRadius: 2,
            bgcolor: greyedOut ? "grey.100" : "background.paper",
            color: greyedOut ? "text.disabled" : "text.primary",
            opacity: greyedOut ? 0.65 : 1,
            filter: greyedOut ? "grayscale(1)" : "none",
          }}
        >
          {title && (
            <Typography variant="h6" sx={{ mb: 3 }}>
              {title}
            </Typography>
          )}
          {!merchant ? (
            <Box
              sx={{
                minHeight: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                border: "1px dashed",
                borderColor: "grey.300",
                borderRadius: 1,
              }}
            >
              <Typography>{emptyMessage}</Typography>
            </Box>
          ) : (
            <Grid container spacing={1.5} alignItems="stretch">
              {MERCHANT_FIELDS.map((field) => {
                const changed =
                  comparisonSide &&
                  (forceHighlightAll ||
                    comparableValue(field, merchant) !==
                      comparableValue(field, comparisonMerchant));
                const highlightColor =
                  comparisonSide === "before" ? "#fff1f0" : "#f0fff4";
                const borderColor =
                  comparisonSide === "before" ? "#ffccc7" : "#b7eb8f";

                return (
                  <React.Fragment key={field.key}>
                    <Grid
                      item
                      xs={5}
                      sx={{
                        p: 1,
                        bgcolor: changed ? highlightColor : "transparent",
                        borderLeft: changed ? `3px solid ${borderColor}` : "3px solid transparent",
                      }}
                    >
                      <Typography variant="subtitle1">{field.label}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={7}
                      sx={{
                        p: 1,
                        overflowWrap: "anywhere",
                        bgcolor: changed ? highlightColor : "transparent",
                        borderRight: changed ? `3px solid ${borderColor}` : "3px solid transparent",
                      }}
                    >
                      {renderFieldValue(field, merchant)}
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>
          )}
        </Box>
      </Grid>
    );
  };

  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";
    const formatTimestamp = isHistoryView ? parseDateTime : parseDate;

    return (
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Deleted"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action === "true" ? (
                <Label
                  variant="ghost"
                  color={merchantDetails.is_approved ? "success" : "error"}
                >
                  {merchantDetails.is_approved ? "YES" : "NO"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={merchantDetails.is_active ? "success" : "error"}
                >
                  {merchantDetails.is_active ? "NO" : "YES"}
                </Label>
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantDetails.created_at
                ? formatTimestamp(merchantDetails.created_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantDetails.created_by
                ? merchantDetails.created_by.email
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantDetails.approved_at
                ? formatTimestamp(merchantDetails.approved_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantDetails.approved_by
                ? merchantDetails.approved_by.email
                : "N/A"}
            </Grid>
            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated on</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {parseDate(merchantDetails.updated_at)}
                </Grid>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {merchantDetails.updated_by
                    ? merchantDetails.updated_by.email
                    : "N/A"}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  const normalizedAction = merchantDetails?.action?.trim().toLowerCase();
  const comparisonActions = ["add", "update", "remove", "suspend", "unsuspend"];
  // Requests reviewed before change logs existed have no truthful "before" to show.
  // Only Update needs it: the other actions compare against the request's own copy.
  const originalUnavailable =
    normalizedAction === "update" &&
    Boolean(merchantDetails?.original_unavailable);
  // action is truthy for every maker-and-checker record: the pending dialog, the
  // history dialog and the standalone history page (opened with ?action=true).
  const showComparison =
    Boolean(action) &&
    comparisonActions.includes(normalizedAction) &&
    !originalUnavailable;
  const originalMerchant = merchantDetails?.original || null;
  const isAddAction = normalizedAction === "add";
  const isRemoveAction = normalizedAction === "remove";
  const isSuspendAction = normalizedAction === "suspend";
  const isUnsuspendAction = normalizedAction === "unsuspend";
  const useMainDataForBefore =
    isRemoveAction || isSuspendAction || isUnsuspendAction;
  const beforeMerchant = isAddAction
    ? null
    : useMainDataForBefore
    ? merchantDetails
    : originalMerchant;
  const afterMerchant = isRemoveAction
    ? null
    : isSuspendAction || isUnsuspendAction
    ? {
        ...merchantDetails,
        is_suspended: isSuspendAction,
      }
    : merchantDetails
    ? {
        ...(originalMerchant || {}),
        ...merchantDetails,
        ...(isAddAction && merchantDetails.is_suspended === undefined
          ? { is_suspended: false }
          : {}),
      }
    : null;

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="Merchant Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && merchantDetails && action && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">
            Action: {merchantDetails.action && merchantDetails.action}
          </Typography>
          {showComparison && !isRemoveAction && (
            <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 14, height: 14, bgcolor: "#fff1f0", border: "1px solid #ffccc7" }} />
                <Typography variant="body2">Changed before value</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 14, height: 14, bgcolor: "#f0fff4", border: "1px solid #b7eb8f" }} />
                <Typography variant="body2">Changed after value</Typography>
              </Stack>
            </Stack>
          )}
        </Box>
      )}
      {loaded && merchantDetails ? (
        <>
          <Card sx={{ p: 3 }}>
            <Grid
              container
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2, md: 2 }}
              sx={{ mt: showComparison ? 0 : 5 }}
            >
              {showComparison ? (
                <>
                  <InfoColumn
                    merchant={beforeMerchant}
                    comparisonMerchant={afterMerchant}
                    title="Before"
                    emptyMessage="No existing merchant"
                    comparisonSide={isRemoveAction ? undefined : "before"}
                  />
                  <InfoColumn
                    merchant={afterMerchant}
                    comparisonMerchant={beforeMerchant}
                    title="After"
                    emptyMessage="Merchant will be removed"
                    greyedOut={isSuspendAction}
                    comparisonSide="after"
                    forceHighlightAll={isAddAction}
                  />
                  <DateColumn />
                </>
              ) : (
                <>
                  {action && <DateColumn />}
                  <InfoColumn merchant={merchantDetails} />
                  {!action && <DateColumn />}
                </>
              )}
            </Grid>
            {merchantDetails.reject_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {merchantDetails.reject_message}
                </Typography>
              </Typography>
            )}
            {merchantDetails.action_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Action message:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {merchantDetails.action_message}
                </Typography>
              </Typography>
            )}
          </Card>
          {
            !detailsData && (
              <Button
                variant="outlined"
                sx={{ mx: 3, my: 2 }}
                onClick={() =>
                  window.open(
                    `/3/app/transactions?merchant_name=${merchantDetails.name}`,
                    "_blank"
                  )
                }
              >
                Transactions
              </Button>
            )
          }
        </>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {tableData && (
        <>
          <Divider sx={{ mb: 5 }} />
          <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
            Tagged Terminals
          </Typography>
          <Card>
            <TableView
              rows={tableData}
              count={taggedTerminals.count}
              link={"/3/app/terminal-details~id~"}
            />
          </Card>
        </>
      )}

      {selectedId && 
        <EditDialog
          open={true}
          onClose={handleCloseDialog}
          selectedId={selectedId}
        />
      }
    </Container>
  );
};

export default MerchantDetails;
